import React, { useCallback } from 'react';
import { Settings, Link, Hash, MessageCircle } from 'lucide-react';
import { SlackWizardConfig } from '../SlackWizard';

interface SendStepProps {
  nodeId: string;
  config: SlackWizardConfig;
  onChange: (updates: Partial<SlackWizardConfig>) => void;
  errors: string[];
}

export function SendStep({
  nodeId,
  config,
  onChange,
  errors,
}: SendStepProps) {
  const handleOptionsChange = useCallback((options: Partial<SlackWizardConfig['options']>) => {
    onChange({
      options: {
        ...config.options,
        ...options,
      },
    });
  }, [config.options, onChange]);

  const handleOutputsChange = useCallback((outputs: Partial<SlackWizardConfig['outputs']>) => {
    onChange({
      outputs: {
        ...config.outputs,
        ...outputs,
      },
    });
  }, [config.outputs, onChange]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Send Options & Outputs
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Configure message delivery options and specify which data to expose for downstream nodes.
        </p>
      </div>

      {/* Message Options */}
      <div className="space-y-4">
        <div className="flex items-center">
          <Settings className="w-5 h-5 text-gray-400 mr-2" />
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Message Options
          </h4>
        </div>

        <div className="space-y-3 ml-7">
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="unfurl-links" className="text-sm font-medium text-gray-900 dark:text-white">
                Unfurl Links
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Automatically expand link previews in messages
              </p>
            </div>
            <input
              type="checkbox"
              id="unfurl-links"
              checked={config.options.unfurlLinks}
              onChange={(e) => handleOptionsChange({ unfurlLinks: e.target.checked })}
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="unfurl-media" className="text-sm font-medium text-gray-900 dark:text-white">
                Unfurl Media
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Automatically expand media previews (images, videos)
              </p>
            </div>
            <input
              type="checkbox"
              id="unfurl-media"
              checked={config.options.unfurlMedia}
              onChange={(e) => handleOptionsChange({ unfurlMedia: e.target.checked })}
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="link-names" className="text-sm font-medium text-gray-900 dark:text-white">
                Link Names
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Convert @mentions to clickable user links
              </p>
            </div>
            <input
              type="checkbox"
              id="link-names"
              checked={config.options.linkNames}
              onChange={(e) => handleOptionsChange({ linkNames: e.target.checked })}
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
            />
          </div>
        </div>
      </div>

      {/* Output Configuration */}
      <div className="space-y-4">
        <div className="flex items-center">
          <MessageCircle className="w-5 h-5 text-gray-400 mr-2" />
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Output Configuration
          </h4>
        </div>

        <div className="space-y-3 ml-7">
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="expose-permalinks" className="text-sm font-medium text-gray-900 dark:text-white">
                Expose Permalinks
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Include message permalinks in output data
              </p>
            </div>
            <input
              type="checkbox"
              id="expose-permalinks"
              checked={config.outputs.exposePermalinks}
              onChange={(e) => handleOutputsChange({ exposePermalinks: e.target.checked })}
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="expose-channel-ids" className="text-sm font-medium text-gray-900 dark:text-white">
                Expose Channel IDs
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Include Slack channel IDs in output data
              </p>
            </div>
            <input
              type="checkbox"
              id="expose-channel-ids"
              checked={config.outputs.exposeChannelIds}
              onChange={(e) => handleOutputsChange({ exposeChannelIds: e.target.checked })}
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="expose-channel-names" className="text-sm font-medium text-gray-900 dark:text-white">
                Expose Channel Names
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Include human-readable channel names in output data
              </p>
            </div>
            <input
              type="checkbox"
              id="expose-channel-names"
              checked={config.outputs.exposeChannelNames}
              onChange={(e) => handleOutputsChange({ exposeChannelNames: e.target.checked })}
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
            />
          </div>
        </div>
      </div>

      {/* Output Data Preview */}
      <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Expected Output Data Structure
        </h4>
        <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
          This data will be available to downstream nodes as {'{{slack.*}}'}:
        </div>
        <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono bg-white dark:bg-gray-800 rounded p-3 border">
{`{
  "messages": [
    {
      "channel": "C1234567890",${config.outputs.exposeChannelNames ? '\n      "channelName": "#general",' : ''}
      "ts": "1234567890.123456",${config.threading.mode === 'start_new' && config.threading.exposeTs ? '\n      "thread_ts": "1234567890.123456",' : ''}${config.outputs.exposePermalinks ? '\n      "permalink": "https://workspace.slack.com/archives/...",' : ''}
      "success": true
    }
  ],
  "summary": {
    "totalSent": ${config.channels.length},
    "totalFailed": 0,
    "channels": [${config.channels.map(c => `"${c}"`).join(', ')}]
  }
}`}
        </pre>
      </div>

      {/* Configuration Summary */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
          Configuration Summary
        </h4>
        <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
          <div className="flex items-center">
            <Hash className="w-4 h-4 mr-2" />
            <span className="font-medium">Channels:</span>
            <span className="ml-2">{config.channels.join(', ')}</span>
          </div>
          <div className="flex items-center">
            <MessageCircle className="w-4 h-4 mr-2" />
            <span className="font-medium">Format:</span>
            <span className="ml-2 capitalize">{config.format}</span>
          </div>
          <div className="flex items-center">
            <Settings className="w-4 h-4 mr-2" />
            <span className="font-medium">Threading:</span>
            <span className="ml-2 capitalize">{config.threading.mode.replace('_', ' ')}</span>
          </div>
          {config.threading.mode === 'reply' && config.threading.threadTs && (
            <div className="flex items-center ml-6">
              <span className="font-medium">Thread TS:</span>
              <span className="ml-2 font-mono text-xs">{config.threading.threadTs}</span>
            </div>
          )}
        </div>
      </div>

      {/* Available Variables */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md p-4">
        <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-3">
          Available Variables for Downstream Nodes
        </h4>
        <div className="space-y-1 text-sm text-green-800 dark:text-green-200">
          <div className="font-mono">{'{{slack.messages[0].channel}}'} - First channel ID</div>
          <div className="font-mono">{'{{slack.messages[0].ts}}'} - First message timestamp</div>
          {config.outputs.exposePermalinks && (
            <div className="font-mono">{'{{slack.messages[0].permalink}}'} - First message link</div>
          )}
          {config.threading.mode === 'start_new' && config.threading.exposeTs && (
            <div className="font-mono">{'{{slack.messages[0].thread_ts}}'} - Thread timestamp</div>
          )}
          <div className="font-mono">{'{{slack.summary.totalSent}}'} - Number of messages sent</div>
        </div>
      </div>

      {/* Error Display */}
      {errors.length > 0 && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex">
            <MessageCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Configuration Issues
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
